import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box, Typography } from '@mui/material';
import { DataProvider, useData } from './context/DataContext';
import { Sidebar, TopNav, ResetDataButton } from './components/Layout';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import type { AccountingProduct } from './context/DataContext';
import { normalizeInternalCodePrefix } from './utils/codeMatching';
import { ProductsPage } from './pages/ProductsPage';
import { SupplierPriceBooksPage } from './pages/SupplierPriceBooksPage';
import { POPage } from './pages/POPage';
import { POListPage } from './pages/POListPage';
import { POPrintPage } from './pages/POPrintPage';
import { POFindBillPage } from './pages/POFindBillPage';
import { QuotePage } from './pages/QuotePage';
import { QuoteListPage } from './pages/QuoteListPage';
import { InvoicePage } from './pages/InvoicePage';
import { InvoiceListPage } from './pages/InvoiceListPage';
import { BillDiffPage } from './pages/BillDiffPage';
import { BillsListPage } from './pages/BillsListPage';
import { BillImportPage } from './pages/BillImportPage';
import { BillPage } from './pages/BillPage';
import { BillNewPage } from './pages/BillNewPage';
import { StatementImportPage } from './pages/StatementImportPage';
import { StatementsListPage } from './pages/StatementsListPage';
import JobsListPage from './pages/JobsListPage';
import { TimeAndMaterialsPage } from './pages/TimeAndMaterialsPage';
import { SchedulePage } from './pages/SchedulePage';
import { SalesDocumentSettingsPage } from './pages/SalesDocumentSettingsPage';
import { SalesTemplateSettingsPage } from './pages/SalesTemplateSettingsPage';
import { BRAND, BRAND_LIGHT } from './theme/tokens';

const theme = createTheme({
  palette: {
    primary:   { main: BRAND },
    secondary: { main: '#F57C00' },
  },
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  components: {
    MuiButton: { styleOverrides: { root: { borderRadius: 4, fontWeight: 600 } } },
    MuiChip:   { styleOverrides: { root: { borderRadius: 4 } } },
    /*
      P3.2 — loading + skeleton states inherit BRAND.
      • Spinners default to brand purple unless an explicit `color` prop overrides.
      • LinearProgress default uses the brand-light track (#F5EEFF) + brand-purple
        bar so the parse processor strip / queue load / statement reconcile
        spinners all read as "FieldInsight is working" instead of MUI default blue.
      • Skeleton default uses brand-light pulse — feels like the brand is
        loading, not a generic placeholder.
      Per-instance `sx` overrides still win — keep that escape hatch for the
      few places that legitimately need a different track colour.
    */
    MuiCircularProgress: {
      defaultProps: { color: 'primary' },
      styleOverrides: { circle: { strokeLinecap: 'round' } },
    },
    MuiLinearProgress: {
      defaultProps: { color: 'primary' },
      styleOverrides: {
        root:                 { backgroundColor: BRAND_LIGHT, borderRadius: 3 },
        bar:                  { backgroundColor: BRAND, borderRadius: 3 },
        colorPrimary:         { backgroundColor: BRAND_LIGHT },
        barColorPrimary:      { backgroundColor: BRAND },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: {
          backgroundColor: BRAND_LIGHT,
          '&::after': {
            background: `linear-gradient(90deg, transparent, ${BRAND}1A, transparent)`,
          },
        },
      },
    },
  },
});

const APP_NAME = 'FieldInsight · AI Bill Processing';

const ROUTE_TITLES: Array<[RegExp, string]> = [
  [/^\/settings\/documents/,  'Document Settings'],
  [/^\/settings\/templates/,   'Template Settings'],
  [/^\/bills\/import/,        'Supplier Invoice Reconciliation'],
  [/^\/bills\/[^/]+$/,        'Bill Detail'],
  [/^\/bills$/,               'Bills'],
  [/^\/bill-diff/,            'Bill Diff'],
  [/^\/statements\/[^/]+/,    'Statement Reconciliation'],
  [/^\/statements$/,          'Statements'],
  [/^\/statement-import/,     'Statement Import (legacy)'],
  [/^\/purchase-orders-list/, 'Purchase Orders'],
  [/^\/purchase-orders/,      'Purchase Orders'],
  [/^\/po-print/,             'PO Print'],
  [/^\/quotes-list/,          'Quotes'],
  [/^\/quotes/,               'Quote'],
  [/^\/invoices-list/,        'Invoices'],
  [/^\/invoices/,             'Invoice'],
  [/^\/supplier-price-books/, 'Supplier Price Books'],
  [/^\/products/,             'Products'],
  [/^\/schedule/,             'Schedule'],
  [/^\/jobs/,                 'Jobs'],
  [/^\/time-and-materials/,   'Time & Materials'],
];

/**
 * v1.14.6 — /statements/:id renders the Statement Verification flow
 * (StatementImportPage). This is the "I want to confirm we have a PO + Bill
 * for every line the supplier claims" workflow. It does NOT create new Bills.
 * For the "I want to ingest these as new Bills" workflow the user navigates
 * to /bills/import?statement=:id instead (BillImportPage). The two flows
 * are separate and cross-linked.
 *
 * Earlier (v1.13–v1.14.5) /statements/:id redirected to /bills/import?statement=:id
 * — that conflated the two workflows. Restored on user request.
 */

/**
 * v1.13 — Enforces the 1-to-1 Statement <-> Bill list invariant.
 * Hitting /bills/import directly (no ?statement= param) used to surface the
 * global, stacked queue. Now we bounce to /statements so users always enter
 * the bill workflow via a parent Statement (auto-created on upload if they
 * had no statement open).
 */
const BillImportGate: React.FC = () => {
  const [params] = useSearchParams();
  if (!params.get('statement')) {
    return <Navigate to="/statements" replace />;
  }
  return <BillImportPage />;
};

function useDocumentTitle() {
  const { pathname } = useLocation();
  useEffect(() => {
    const match = ROUTE_TITLES.find(([re]) => re.test(pathname));
    document.title = match ? `${match[1]} · ${APP_NAME}` : APP_NAME;
  }, [pathname]);
}

function TrackSupplierCodesToggle() {
  const { trackSupplierCodes, setTrackSupplierCodes } = useData();
  return (
    <Tooltip
      title={trackSupplierCodes
        ? 'ON — show the Supplier Code column on POs/Bills and keep supplier↔inventory links in the price book. Import preview still asks: use supplier codes, or auto-create unique internal codes.'
        : 'OFF — hide the Supplier Code column. Import preview still offers: use supplier codes as inventory, or auto-create unique internal codes (FIE-234…).'}
      placement="top"
    >
      <FormControlLabel
        control={
          <Switch size="small" checked={trackSupplierCodes}
            onChange={e => setTrackSupplierCodes(e.target.checked)}
            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#6a3fc0' },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#6a3fc0' } }} />
        }
        label={
          <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#999' }}>
            Show Supplier Code column
          </Typography>
        }
        sx={{ m: 0, mr: 1 }}
      />
    </Tooltip>
  );
}

function InternalCodePrefixSetting() {
  const { internalCodePrefix, setInternalCodePrefix } = useData();
  const [draft, setDraft] = React.useState(internalCodePrefix);
  React.useEffect(() => { setDraft(internalCodePrefix); }, [internalCodePrefix]);
  const commit = () => {
    const next = normalizeInternalCodePrefix(draft);
    setDraft(next);
    setInternalCodePrefix(next);
  };
  return (
    <Tooltip
      title={`Auto-set inventory codes use this 3-letter company prefix — e.g. ${normalizeInternalCodePrefix(draft)}-234. FieldInsight → FIE.`}
      placement="top"
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#999', whiteSpace: 'nowrap' }}>
          Internal code
        </Typography>
        <TextField
          size="small"
          value={draft}
          onChange={e => setDraft(e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3))}
          onBlur={commit}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commit(); } }}
          inputProps={{
            maxLength: 3,
            'aria-label': 'Internal inventory code prefix',
            style: {
              fontFamily: 'monospace',
              fontWeight: 800,
              fontSize: '0.72rem',
              textAlign: 'center',
              letterSpacing: 1,
              width: 36,
              padding: '2px 4px',
            },
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              height: 24,
              backgroundColor: '#fff',
              '& fieldset': { borderColor: '#d0c5f0' },
              '&:hover fieldset': { borderColor: '#6a3fc0' },
              '&.Mui-focused fieldset': { borderColor: '#6a3fc0' },
            },
          }}
        />
        <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.62rem', color: '#bbb' }}>
          -234
        </Typography>
      </Box>
    </Tooltip>
  );
}

function AutoLinkBillReferenceToggle() {
  const { autoLinkBillReference, setAutoLinkBillReference } = useData();
  return (
    <FormControlLabel
      control={
        <Switch size="small" checked={autoLinkBillReference}
          onChange={e => setAutoLinkBillReference(e.target.checked)}
          sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#6a3fc0' },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#6a3fc0' } }} />
      }
      label={<Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#999' }}>Auto-link bills from reference</Typography>}
      sx={{ m: 0, mr: 1 }}
    />
  );
}

function AccountingProductSelect() {
  const { accountingProduct, setAccountingProduct } = useData();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#999' }}>Pay via</Typography>
      <Select
        size="small"
        value={accountingProduct}
        onChange={e => setAccountingProduct(e.target.value as AccountingProduct)}
        sx={{ fontSize: '0.65rem', height: 24, '& .MuiSelect-select': { py: 0.25, pr: '20px !important' } }}
      >
        {(['Xero', 'MYOB', 'QuickBooks'] as AccountingProduct[]).map(p => (
          <MenuItem key={p} value={p} sx={{ fontSize: '0.7rem' }}>{p}</MenuItem>
        ))}
      </Select>
    </Box>
  );
}

function Layout({ children }: { children: React.ReactNode }) {
  useDocumentTitle();
  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Sidebar />
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
        <TopNav />
        {/* Main scroll — full page scrolls here (right-side scrollbar) */}
        <Box component="main" sx={{ flex: 1, overflow: 'auto', minHeight: 0, p: 3, backgroundColor: '#f9f9f9' }}>
          {children}
        </Box>
        {/* Version footer */}
        <Box sx={{ px: 3, py: 0.5, borderTop: '1px solid #e0e0e0', backgroundColor: '#fafafa',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" sx={{ color: '#bbb', fontSize: '0.65rem' }}>
            Multi-Supplier PO Prototype — <strong style={{ color: '#999' }}>v{process.env.REACT_APP_VERSION ?? '2.0.4'}</strong>
            {process.env.REACT_APP_GIT_SHA ? <span style={{ color: '#ccc' }}> · {process.env.REACT_APP_GIT_SHA}</span> : ''}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AccountingProductSelect />
            <AutoLinkBillReferenceToggle />
            <InternalCodePrefixSetting />
            <TrackSupplierCodesToggle />
            <ResetDataButton />
            <Typography variant="caption" sx={{ color: '#bbb', fontSize: '0.65rem' }}>
              Northbridge Diesel & Mechanical · Built by Alfred
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default function App() {
  return (
    <DataProvider>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Layout>
          <Routes>
            <Route path="/"                       element={<Navigate to="/products" replace />} />
            <Route path="/products"               element={<ProductsPage />} />
            <Route path="/supplier-price-books"   element={<SupplierPriceBooksPage />} />
            <Route path="/purchase-orders"        element={<POPage />} />
            <Route path="/purchase-orders-list"   element={<POListPage />} />
            <Route path="/po-print/:id"           element={<POPrintPage />} />
            <Route path="/po-print/:id/find-bill" element={<POFindBillPage />} />
            <Route path="/quotes-list"            element={<QuoteListPage />} />
            <Route path="/quotes/:id"             element={<QuotePage />} />
            <Route path="/quotes"                 element={<Navigate to="/quotes-list" replace />} />
            <Route path="/invoices-list"          element={<InvoiceListPage />} />
            <Route path="/invoices/:id"           element={<InvoicePage />} />
            <Route path="/invoices"              element={<Navigate to="/invoices-list" replace />} />
            <Route path="/bill-diff"              element={<BillDiffPage />} />
            <Route path="/bills"                  element={<BillsListPage />} />
            <Route path="/bills/import"           element={<BillImportGate />} />
            <Route path="/bills/new"              element={<BillNewPage />} />
            <Route path="/bills/:id"              element={<BillPage />} />
            {/* Legacy statement-reconciliation routes — folded into Bill Importer.
                /statements/:id now redirects to /bills/import?statement=:id which
                renders the unified Supplier Invoice Reconciliation UI scoped to
                that statement. /statement-import kept as legacy fallback. */}
            <Route path="/statements"             element={<StatementsListPage />} />
            {/* Statement VERIFICATION flow — confirms each statement line
                has a PO+Bill, marks variances, does NOT create bills. */}
            <Route path="/statements/:id"         element={<StatementImportPage />} />
            <Route path="/statement-import"       element={<StatementImportPage />} />
            <Route path="/settings/documents/:docType" element={<SalesDocumentSettingsPage />} />
            <Route path="/settings/templates/:docType"  element={<SalesTemplateSettingsPage />} />
            <Route path="/schedule"               element={<SchedulePage />} />
            <Route path="/jobs"                   element={<JobsListPage />} />
            <Route path="/time-and-materials"     element={<TimeAndMaterialsPage />} />
            <Route path="*"                       element={<Navigate to="/products" replace />} />
          </Routes>
        </Layout>
      </Router>
    </ThemeProvider>
    </DataProvider>
  );
}
